/****************
 * WHERE A TRADER COMES FROM IS FOR ADMINS ONLY.
 *
 * Guillaume, 2026-10-01, on a line that read "Margin mode not collected for
 * MEXC yet": it "let people know the source of the traders when they should
 * not". And then the rule: "Source should only be visible by admins."
 *
 * The exchange a trader is on reaches a screen three ways, and only the first
 * looks like one:
 *
 *   1. a sentence or a label that names it          (the line above; the venue
 *                                                    chooser; "Open on ...")
 *   2. THE ID, printed where a name was expected    "mexc:01146489" says it in
 *                                                    its first four letters
 *   3. a diagnostic that states its own request     "ZECUSDT · mexc · ..."
 *
 * The first is gated at each place it is rendered (canChooseVenue,
 * VenueTraderId behind isAdmin). This file is the other two: the one answer to
 * "what do I print when the name is missing" and "what does the chart say it
 * asked for", so that no screen has to remember the rule on its own.
 * traderSource.test.ts reads the source of every screen and fails when one
 * goes back to printing the id.
 *
 * The user's OWN broker is not a secret and nothing here touches it.
 ****************/

/** An id as an exchange or our collector writes it, not a name a person reads:
 *  "mexc:01146489", "binance:4740...", "hyperliquid:0xabc...", or a bare run of
 *  eight or more digits (a Binance lead portfolio id). */
export function looksLikeATraderId(s: string): boolean {
  const v = (s || '').trim();
  if (!v) return false;
  if (/^[a-z]{2,}:\S+$/i.test(v)) return true;
  if (/^0x[0-9a-f]{6,}$/i.test(v)) return true;
  return /^\d{8,}$/.test(v);
}

/** A name a person reads and an address can carry: not empty, not an id, and
 *  not the "#6489" stand-in shownTraderName gives when there is no name. */
export function isRealTraderName(name: string | null | undefined): boolean {
  const n = (name || '').trim();
  return !!n && !n.startsWith('#') && !looksLikeATraderId(n) && !/^SIM_/i.test(n);
}

/**
 * What to call a trader on screen.
 *
 * The name when there is one. When there is not -- the roster had not loaded,
 * the trader left it, the server sent the id in the name field -- an ADMIN gets
 * the id, because that is what they need to look the trader up, and everyone
 * else gets a mark that says "a trader, this one" without saying where:
 * "#6489", the id's last four characters. Not the first eight, which is what
 * the dashboard printed before and which for a MEXC trader is "mexc:011".
 *
 * Our own simulated traders (SIM_...) keep their id: it is a name we wrote and
 * it names no exchange.
 */
export function shownTraderName(
  name: string | null | undefined,
  uid: string | null | undefined,
  isAdmin: boolean = false,
): string {
  const n = (name || '').trim();
  if (n && !looksLikeATraderId(n)) return n;
  const id = (uid || n || '').trim();
  if (!id) return '';
  if (isAdmin) return id;
  if (/^SIM_/i.test(id)) return id;
  const tail = id.replace(/^[a-z]{2,}:/i, '').replace(/[^0-9a-z]/gi, '').slice(-4);
  return tail ? `#${tail}` : '';
}

/**
 * The venue segment of the chart's "what I asked for" line, with its
 * separator. For an admin it is the venue the candles were requested from --
 * which is the exchange the trader is on. For everyone else it is nothing: the
 * line still carries the symbol, the window and the candle count.
 */
export function chartRequestVenue(isAdmin: boolean, venue: string | null | undefined): string {
  if (!isAdmin) return '';
  return `${venue || 'default venue'} · `;
}
